import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import React, { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { DEFAULT_HOME_PATH, GOOGLE_CALENDAR_SYNC_ENABLED } from "./config";
import { SubscriptionProvider } from "./hooks/use-subscription";
import { LayoutProvider } from "./contexts/layout-context";
import { LoadingScreen } from "./components/loading-screen";
import { SentryUserSync } from "./components/sentry-user-sync";
import { RouteErrorFallback } from "./components/error-boundary";

const Login = lazy(() => import("./pages/login"));
const Calendar = lazy(() => import("./pages/calendar"));
const Pazienti = lazy(() => import("./pages/pazienti"));
const NuovoPaziente = lazy(() => import("./pages/nuovo-paziente"));
const PazienteEdit = lazy(() => import("./pages/paziente/[id]"));
const Indirizzi = lazy(() => import("./pages/indirizzi"));
const NuovoIndirizzo = lazy(() => import("./pages/nuovo-indirizzo"));
const EditIndirizzo = lazy(() => import("./pages/indirizzi/[id]"));
const Impostazioni = lazy(() => import("./pages/impostazioni"));
const Piani = lazy(() => import("./pages/piani"));
const LibreriaEsercizi = lazy(() => import("./pages/libreria-esercizi"));
const ListaSchede = lazy(() => import("./pages/lista-schede"));

const GoogleCallback = GOOGLE_CALENDAR_SYNC_ENABLED
  ? lazy(() => import("./pages/google-callback"))
  : null;
const DashboardLayout = lazy(() =>
  import("./layouts/dashboard-layout").then((m) => ({
    default: m.DashboardLayout,
  })),
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dati considerati "freschi" per 2 minuti: nessun refetch inutile
      // al ritorno su una pagina già visitata di recente.
      // Le query che richiedono dati sempre aggiornati (es. agenda)
      // sovrascrivono con staleTime: 0 a livello di hook.
      staleTime: 2 * 60 * 1000,
      retry: 1,
    },
  },
});

const withSuspense = (element: React.ReactNode) => (
  <Suspense fallback={<LoadingScreen />}>{element}</Suspense>
);

const router = createBrowserRouter([
  {
    errorElement: <RouteErrorFallback />,
    children: [
      { path: "/login", element: withSuspense(<Login />) },
      {
        element: <DashboardLayout />,
        children: [
          {
            path: "/",
            element: <Navigate to={DEFAULT_HOME_PATH} replace />,
          },
          { path: "/agenda", element: withSuspense(<Calendar />) },
          { path: "/pazienti", element: withSuspense(<Pazienti />) },
          { path: "/pazienti/nuovo", element: withSuspense(<NuovoPaziente />) },
          { path: "/paziente/:id", element: withSuspense(<PazienteEdit />) },
          { path: "/indirizzi", element: withSuspense(<Indirizzi />) },
          { path: "/indirizzi/nuovo", element: withSuspense(<NuovoIndirizzo />) },
          { path: "/indirizzi/:id", element: withSuspense(<EditIndirizzo />) },
          { path: "/impostazioni", element: withSuspense(<Impostazioni />) },
          { path: "/piani", element: withSuspense(<Piani />) },
          { path: "/libreria-esercizi", element: withSuspense(<LibreriaEsercizi />) },
          { path: "/lista-schede", element: withSuspense(<ListaSchede />) },
        ],
      },
      ...(GOOGLE_CALENDAR_SYNC_ENABLED && GoogleCallback
        ? [{ path: "/google-callback", element: withSuspense(<GoogleCallback />) }]
        : []),
      { path: "*", element: <Navigate to={DEFAULT_HOME_PATH} replace /> },
    ],
  },
]);

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <SubscriptionProvider>
        <SentryUserSync />
        <LayoutProvider>
          <Suspense fallback={<LoadingScreen />}>
            <RouterProvider router={router} />
          </Suspense>
        </LayoutProvider>
      </SubscriptionProvider>
    </QueryClientProvider>
  );
}

export default App;
